import { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import { Button, Col, Form, Input, Radio, Row, Tag, Typography } from 'antd';
import {
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
} from '@ant-design/icons';

import {
  CustomNotification,
  FormButton,
  FormDeleteButton,
  dialogFunction,
} from 'common/components';
import PreviewableModal from 'common/components/previewable-modal/PreviewableModal';
import { useProductDetail } from 'pages/product-full-view/components/product-new-detail-view/ProductDetailContext';
import {
  FormItemContainer,
  PropertyLabel,
} from 'pages/product-full-view/shared/components';
import { getDataByFieldFullPath } from 'pages/product-full-view/shared/utils';
import * as productService from 'services/product';
import { apiHandler } from 'utils/api';
import * as api from 'config/axios';
import { checkShowPdfPreview } from 'utils/checkShowPdfPreview';
import { useGetSdsDataSheet } from 'hooks/product/useGetSdsDataSheet';

import SdsDataSheetModal from 'pages/product-full-view/components/modal/sds-data-sheet-modal/SdsDataSheetModal';

// Derives the Form.Item name for the sibling SdsDataSheetsLinkId field from
// this field's own name. When nested (name is an array, e.g. rendered inside
// a single-nested group like ComplianceOther via FormSingleNestedValue), the
// link id lives alongside it at the same nesting level. When flat (name is
// undefined, e.g. rendered directly by ProductDetailEdit's top-level
// dispatch), it's just the bare top-level field name.
const getLinkIdFieldName = (name) => {
  if (Array.isArray(name)) {
    return [...name.slice(0, -1), 'SdsDataSheetsLinkId'];
  }
  return 'SdsDataSheetsLinkId';
};

// Rendered as the sole child of FormItemContainer so antd's Form.Item can
// clone `value`/`onChange` onto it directly (the way FormItemBoolean relies
// on Radio.Group being its only child). We forward those onto Radio.Group
// ourselves so the Load button/status can live alongside it in one row.
const SdsSheetRequiredControl = ({ value, onChange, name, ...restProps }) => {
  const { formInstance } = useProductDetail();
  const { id: productId } = useParams();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const linkIdFieldName = getLinkIdFieldName(name);

  const linkedId = Form.useWatch(linkIdFieldName, formInstance);

  const { data: linkedFile } = useGetSdsDataSheet({
    productId,
    linkId: linkedId,
  });

  // If a file was already linked in a previous save, hydrate the tag/status
  // from the fetched file once, so the user doesn't have to reopen the
  // modal and reselect it. Skipped once the user has interacted locally
  // (Submit/Delete already keep `selectedFile` in sync).
  useEffect(() => {
    if (!selectedFile && linkedFile) {
      setSelectedFile(linkedFile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedFile]);

  const removeSelectedFile = async () => {
    const params = {
      linkId: selectedFile.linkId,
      productItemId: productId,
      digitalAssetId: selectedFile.digitalAssetId,
    };

    await apiHandler({
      service: productService.unlinkProductDocument,
      params,
      successMessage: 'Unlink successfully!',
      errorMessage: 'Unlink fail!',
      failCallback: (message) => {
        CustomNotification.error(message);
      },
      successCallback: () => {
        formInstance.setFields([{ name: linkIdFieldName, value: null }]);
        setSelectedFile(null);
      },
    });
  };

  const handleRemoveSelectedFile = () => {
    dialogFunction({
      type: 'warn',
      content: 'Are you sure you want to remove this SDS sheet?',
      okText: 'OK',
      cancelText: 'Cancel',
      onOk: () => removeSelectedFile(),
    });
  };

  const handleChange = (event) => {
    if (event.target.value === false) {
      formInstance.setFields([{ name: linkIdFieldName, value: null }]);
      setSelectedFile(null);
    }
    onChange && onChange(event);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Radio.Group value={value} onChange={handleChange} {...restProps}>
          <Radio value={true}>Yes</Radio>
          <Radio value={false}>No</Radio>
        </Radio.Group>
        {value && (
          <FormButton
            text='Load'
            type='secondary'
            style={{ marginLeft: 20 }}
            onClick={() => setIsModalOpen(true)}
          />
        )}
      </div>
      {value && (
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center' }}>
          {selectedFile ? (
            <>
              <Tag icon={<FileTextOutlined />}>{selectedFile.fileName}</Tag>
              <FormDeleteButton
                text={null}
                style={{ marginLeft: 8 }}
                onClick={handleRemoveSelectedFile}
              />
            </>
          ) : (
            <Typography.Text type='secondary'>
              No SDS sheet loaded yet
            </Typography.Text>
          )}
        </div>
      )}
      {/*
        SdsDataSheetsLinkId is intentionally skipped by ProductDetailEdit's/
        FormSingleNestedValue's schema-driven dispatch loops so it never
        renders as a visible field. Register it here instead so
        validateFields()/getFieldsValue() picks it up on save; the SDS modal
        writes to it via formInstance.
      */}
      <Form.Item name={linkIdFieldName} hidden>
        <Input />
      </Form.Item>
      {isModalOpen && (
        <SdsDataSheetModal
          formInstance={formInstance}
          linkIdFieldName={linkIdFieldName}
          onClose={() => setIsModalOpen(false)}
          onSubmit={setSelectedFile}
        />
      )}
    </div>
  );
};

export const FormItemSdsSheetRequired = (props) => {
  const { property, name, ...otherProps } = props;

  return (
    <FormItemContainer
      property={property}
      labelCol={{ span: 6 }}
      {...(name !== undefined ? { name } : {})}
      {...otherProps}
    >
      <SdsSheetRequiredControl name={name} />
    </FormItemContainer>
  );
};

// Read-only (view mode) counterpart: renders the linked file (styled like
// edit mode's tag) instead of the raw SdsDataSheetsLinkId value, plus
// Preview/Download actions.
export const PropertySdsDataSheet = ({ property, data }) => {
  const { id: productId } = useParams();

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const linkId = getDataByFieldFullPath(data, property.fieldFullPath);

  const { data: linkedFile } = useGetSdsDataSheet({ productId, linkId });

  const { linkPreview, extensionFile } = checkShowPdfPreview(
    linkedFile?.pdfPreview,
    linkedFile?.originalFile,
    linkedFile?.fileType,
  );

  const handleDownload = () => {
    if (!linkedFile?.originalFile) {
      CustomNotification.error('Could not download file');
      return;
    }
    api.sendDownload({ url: linkedFile.originalFile });
  };

  if (!linkId) return null;

  return (
    <Row gutter={8}>
      <Col span={12}>
        <PropertyLabel>SDS Sheet:</PropertyLabel>
      </Col>
      <Col span={12} style={{ display: 'flex', alignItems: 'center' }}>
        <Tag icon={<FileTextOutlined />}>{linkedFile?.fileName || '...'}</Tag>
        <Button
          type='link'
          size='small'
          icon={<EyeOutlined />}
          disabled={!linkedFile}
          onClick={() => setIsPreviewOpen(true)}
        />
        <Button
          type='link'
          size='small'
          icon={<DownloadOutlined />}
          disabled={!linkedFile}
          onClick={handleDownload}
        />
      </Col>
      {isPreviewOpen && (
        <PreviewableModal
          id={linkedFile?.linkId}
          link={linkPreview}
          title={linkedFile?.fileName}
          visible={isPreviewOpen}
          fileName={linkedFile?.fileName}
          cancelModal={() => setIsPreviewOpen(false)}
          downloadLink={linkedFile?.originalFile}
          isDownload={false}
          fileType={extensionFile}
          fileSize={linkedFile?.fileSize}
          isDocument={true}
        />
      )}
    </Row>
  );
};
